import { flagEnabled } from "../flags"
import { type DrawableMixin } from "../objects/drawable_mixin"
import PathObject from "../objects/path"
import Form, { type CurveOptions } from "./base"
import Calc from "@/common/util/calculator"
import PolygonObject from "@/signomatic/app/objects/polygon"

export default class RectForm extends Form {
  protected readonly flangedOffset = this.flangedHeight + this.drawables.cutlineWidth * 2

  protected path(width: number, height: number, rounding?: number, curves?: CurveOptions) {
    if (this.drawables.production === "cut") {
      const x = -(width / 2)
      const y = -(height / 2)
      const points = []

      points.push({ x, y })
      points.push({ x: x + width, y })
      points.push({ x: x + width, y: y + height })
      points.push({ x, y: y + height })
      points.push({ x, y })

      return new PolygonObject(points, this.defaultParams)
    }

    return new PathObject(this.commands(width, height, rounding, curves).join(""), this.defaultParams)
  }

  protected commands(width: number, height: number, rounding?: number, curves?: CurveOptions) {
    const [side, rest] = this.snappableCommands(width, height, rounding, curves)
    const commands = [...side]

    rest.shift()
    commands.push(...rest)
    commands.push(this.pathClose())

    return commands
  }

  protected snappablePaths(
    width: number,
    height: number,
    rounding?: number,
    curves?: CurveOptions
  ): [DrawableMixin, DrawableMixin] {
    const [side, rest] = this.snappableCommands(width, height, rounding, curves)

    return [new PathObject(side.join(""), this.defaultParams), new PathObject(rest.join(""), this.defaultParams)]
  }

  private snappableCommands(
    width: number,
    height: number,
    rounding?: number,
    curves?: CurveOptions
  ): [string[], string[]] {
    const startX = -(width / 2)
    const startY = -(height / 2)
    rounding = rounding ? rounding : 0

    // Left side snap
    const side = [this.pathMoveTo(startX, startY + height - rounding)]

    // Left curve
    if (curves?.left) {
      const radius = curves.left
      side.push(this.pathLineVAbs(radius))
      side.push(this.pathArc(radius, radius, 0, -(radius * 2)))
    }

    // line to Top Left
    side.push(this.pathLineVAbs(startY + rounding))

    // rest of the shape
    const rest = [this.pathMoveTo(startX, startY + rounding)]

    rest.push(this.pathArc(rounding, rounding, rounding, -rounding)) // Top Left rounding

    // Top curve
    if (curves?.top) {
      const radius = curves.top
      rest.push(this.pathLineHAbs(-radius))
      rest.push("a " + radius + " " + radius + " 30 0 1 " + radius * 2 + " 0")
    }

    rest.push(this.pathLineHAbs(width / 2 - rounding)) // line to Top Right
    rest.push(this.pathArc(rounding, rounding, rounding, rounding)) // Top Right rounding

    // Right curve
    if (curves?.right) {
      const radius = curves.right
      rest.push(this.pathLineVAbs(-radius))
      rest.push(this.pathArc(radius, radius, 0, radius * 2))
    }

    rest.push(this.pathLineVAbs(height / 2 - rounding)) // line to Bottom Right
    rest.push(this.pathArc(rounding, rounding, -rounding, rounding)) // Bottom Right rounding
    rest.push(this.pathLineHAbs(startX + rounding)) // line to Bottom Left
    rest.push(this.pathArc(rounding, rounding, -rounding, -rounding)) // Bottom Left rounding

    return [side, rest]
  }

  protected flangedPath(width: number, height: number, rounding?: number) {
    if (this.state.fixmethod !== "pole_flanged") {
      return null
    }

    return this.flanged(
      width + this.flangedOverflow * 2,
      height + this.flangedOverflow * 2,
      this.flangedOffset,
      rounding
    )
  }

  protected flangedHoles(width: number, height: number, rounding?: number) {
    rounding = rounding || 0
    const offset = rounding > 0 ? rounding + this.flangedOffset : this.flangedOffset
    const holes: DrawableMixin[] = []
    const flangedBig = this.state.width > 999

    const minHoles = 1
    const holesHorizontal = flangedBig
      ? Math.max(Math.floor(((width - offset * 2) / 2 - this.flangedHoleWidth / 2) / this.flangedHoleOffset), minHoles)
      : 1

    if (flangedBig) {
      holes.push(this.flangedHole(0, -(height / 2) - this.flangedHeight / 2, "horizontal"))
      holes.push(this.flangedHole(0, height / 2 + this.flangedHeight / 2, "horizontal"))
    }

    for (let index = 0; index < holesHorizontal; index++) {
      const offset = index + 1

      holes.push(
        this.flangedHole(
          -((flangedBig ? offset : 1) * this.flangedHoleOffset),
          -(height / 2) - this.flangedHeight / 2,
          "horizontal"
        )
      )
      holes.push(
        this.flangedHole(
          (flangedBig ? offset : 1) * this.flangedHoleOffset,
          -(height / 2) - this.flangedHeight / 2,
          "horizontal"
        )
      )

      holes.push(
        this.flangedHole(
          -((flangedBig ? offset : 1) * this.flangedHoleOffset),
          height / 2 + this.flangedHeight / 2,
          "horizontal"
        )
      )
      holes.push(
        this.flangedHole(
          (flangedBig ? offset : 1) * this.flangedHoleOffset,
          height / 2 + this.flangedHeight / 2,
          "horizontal"
        )
      )
    }

    return holes
  }

  protected cutlinePath(
    width: number,
    height: number,
    rounding?: number,
    curves?: CurveOptions,
    forClip?: boolean
  ): DrawableMixin {
    if (this.state.fixmethod === "pole_flanged" && !forClip) {
      return this.flanged(width, height, this.flangedOffset, rounding)
    }

    return this.path(width, height, rounding, curves)
  }

  protected cutlineGappedPath(width: number, height: number, rounding: number): DrawableMixin | undefined {
    const styles = { stroke: "#f08894", strokeWidth: this.drawables.cutlineWidth, fill: "transparent" }

    if (this.isStainlessSteelSign() || this.isEtchedAluminiumSign()) {
      return new PathObject(this.rectCutLine(width, height, rounding).join(""), {
        ...this.defaultParams,
        ...styles,
      })
    }

    return
  }

  protected rectCutLine(width: number, height: number, rounding: number): string[] {
    const rectCutlineFlag = flagEnabled("rect-cutline-9390")
    const horizontalLonger = width >= height

    const gap = this.gapLength()
    const legLength = Calc.mmToPx(2.5)

    const left = -width / 2
    const top = -height / 2
    const bottom = top + height
    const right = left + width

    if (rectCutlineFlag) {
      const safetyPaddingPx = Math.max(
        Calc.mmToPx(2), // lower bound ≈ 2 mm
        Math.min(Calc.mmToPx(4), Math.min(width, height) / 40) // up to ≈ 4 mm; scales by size
      )

      // straight segment between legs must fit between the two corner arcs with safety
      const legGapW = width - 2 * (rounding + safetyPaddingPx)
      const legGapH = height - 2 * (rounding + safetyPaddingPx)

      if (horizontalLonger) {
        // leg points
        const topCenterX = left + width / 2
        const bottomLeftX = left + (width - legGapW) / 2
        const bottomRightX = right - (width - legGapW) / 2

        // rounded corners
        const topRightX = left + width - rounding
        const rightBottomY = bottom - rounding
        const leftEdgeX = left + rounding

        // top center -> to bottom right
        const segment1 = []
        segment1.push(this.pathMoveTo(topCenterX, top - legLength))
        segment1.push(this.pathLineToAbs(topCenterX, top))
        segment1.push(this.pathLineHAbs(topCenterX + gap))
        segment1.push(this.pathLineHAbs(topRightX))
        segment1.push(this.pathArc(rounding, rounding, rounding, rounding))
        segment1.push(this.pathLineVAbs(rightBottomY))
        segment1.push(this.pathArc(rounding, rounding, -rounding, rounding))
        segment1.push(this.pathLineHAbs(bottomRightX + gap))

        // bottom right -> bottom left
        const segment2 = []
        segment2.push(this.pathMoveTo(bottomRightX, bottom + legLength))
        segment2.push(this.pathLineToAbs(bottomRightX, bottom))
        segment2.push(this.pathLineHAbs(bottomRightX - gap))
        segment2.push(this.pathLineHAbs(bottomLeftX + gap))

        // bottom left -> top center
        const segment3 = []
        segment3.push(this.pathMoveTo(bottomLeftX, bottom + legLength))
        segment3.push(this.pathLineToAbs(bottomLeftX, bottom))
        segment3.push(this.pathLineHAbs(bottomLeftX - gap))
        segment3.push(this.pathLineHAbs(leftEdgeX))
        segment3.push(this.pathArc(rounding, rounding, -rounding, -rounding))
        segment3.push(this.pathLineVAbs(top + rounding))
        segment3.push(this.pathArc(rounding, rounding, rounding, -rounding))
        segment3.push(this.pathLineHAbs(topCenterX - gap))

        return [segment1.join(" "), segment2.join(" "), segment3.join(" ")]
      } else {
        // vertical version, place paired gaps on the longer side
        const leftCenterY = top + height / 2
        const rightTopY = top + (height - legGapH) / 2
        const rightBottomY = bottom - (height - legGapH) / 2
        const bottomRightY = top + height - rounding
        const rightEdgeY = top + rounding
        const bottomEdgeX = left + width - rounding
        const topEdgeX = left + rounding

        const segment1 = []
        segment1.push(this.pathMoveTo(right + legLength, leftCenterY))
        segment1.push(this.pathLineToAbs(right, leftCenterY))
        segment1.push(this.pathLineVAbs(leftCenterY + gap))
        segment1.push(this.pathLineVAbs(bottomRightY))
        segment1.push(this.pathArc(rounding, rounding, -rounding, rounding))
        segment1.push(this.pathLineHAbs(topEdgeX))
        segment1.push(this.pathArc(rounding, rounding, -rounding, -rounding))
        segment1.push(this.pathLineVAbs(rightTopY + gap))

        const segment2 = []
        segment2.push(this.pathMoveTo(left - legLength, rightBottomY))
        segment2.push(this.pathLineToAbs(left, rightBottomY))
        segment2.push(this.pathLineVAbs(rightBottomY - gap))
        segment2.push(this.pathLineVAbs(rightTopY + gap))

        const segment3 = []
        segment3.push(this.pathMoveTo(left - legLength, rightTopY))
        segment3.push(this.pathLineToAbs(left, rightTopY))
        segment3.push(this.pathLineVAbs(rightTopY - gap))
        segment3.push(this.pathLineVAbs(rightEdgeY))
        segment3.push(this.pathArc(rounding, rounding, rounding, -rounding))
        segment3.push(this.pathLineHAbs(bottomEdgeX))
        segment3.push(this.pathArc(rounding, rounding, rounding, rounding))
        segment3.push(this.pathLineVAbs(leftCenterY - gap))

        return [segment1.join(" "), segment2.join(" "), segment3.join(" ")]
      }
    } else {
      // leg points
      const topCenterX = left + width / 2
      const bottomRightX = left + width - width / 4
      const bottomLeftX = left + width / 4

      // rounded corners
      const topRightX = left + width - rounding
      const rightBottomY = bottom - rounding
      const leftEdgeX = left + rounding

      // top center -> to bottom right
      const segment1 = []
      segment1.push(this.pathMoveTo(topCenterX, top - legLength))
      segment1.push(this.pathLineToAbs(topCenterX, top))
      segment1.push(this.pathLineHAbs(topCenterX + gap))
      segment1.push(this.pathLineHAbs(topRightX))
      segment1.push(this.pathArc(rounding, rounding, rounding, rounding))
      segment1.push(this.pathLineVAbs(rightBottomY))
      segment1.push(this.pathArc(rounding, rounding, -rounding, rounding))
      segment1.push(this.pathLineHAbs(bottomRightX + gap))

      // bottom right -> bottom left
      const segment2 = []
      segment2.push(this.pathMoveTo(bottomRightX, bottom + legLength))
      segment2.push(this.pathLineToAbs(bottomRightX, bottom))
      segment2.push(this.pathLineHAbs(bottomRightX - gap))
      segment2.push(this.pathLineHAbs(bottomLeftX + gap))

      // bottom left -> top center
      const segment3 = []
      segment3.push(this.pathMoveTo(bottomLeftX, bottom + legLength))
      segment3.push(this.pathLineToAbs(bottomLeftX, bottom))
      segment3.push(this.pathLineHAbs(bottomLeftX - gap))
      segment3.push(this.pathLineHAbs(leftEdgeX))
      segment3.push(this.pathArc(rounding, rounding, -rounding, -rounding))
      segment3.push(this.pathLineVAbs(top + rounding))
      segment3.push(this.pathArc(rounding, rounding, rounding, -rounding))
      segment3.push(this.pathLineHAbs(topCenterX - gap))

      return [segment1.join(" "), segment2.join(" "), segment3.join(" ")]
    }
  }

  protected get baseRounding() {
    return this.state.shape === "round" ||
      (["label_plates", "stainless_steel_sign"].includes(this.signType) &&
        this.state.material === "stainless" &&
        this.state.shape === "rect") ||
      this.cornerRadiusConditions()
      ? this.printCutlineRounding(this.calcRounding())
      : 0
  }

  protected get cutlineRounding() {
    if (this.state.shape === "round" || this.cornerRadiusConditions()) {
      return this.printCutlineRounding(this.calcRounding())
    }

    if (
      this.isPrint &&
      this.state.fixmethod !== "pole_flanged" &&
      this.state.shape === "rect" &&
      !["magnetic_sign", "forex_sign", "solid_plastic_sign", "poster", "ecoboard", "banner"].includes(this.signType) &&
      !(this.signType === "double_sided_sign" && ["ecoboard", "plastic"].includes(this.state.material))
    ) {
      return this.printCutlineRounding(this.calcRounding())
    }

    if (
      ["label_plates", "stainless_steel_sign"].includes(this.signType) &&
      this.state.material === "stainless" &&
      this.state.shape === "rect"
    ) {
      return this.printCutlineRounding(this.calcRounding())
    }

    return 0
  }

  private calcRounding() {
    const { widthPx, heightPx, height, fixmethod, material } = this.state

    if (this.signType === "aluminium_sign") {
      return Math.min(height / 2, this.drawables.sizeRelative("width", Calc.mmToPx(3), Calc.mmToPx(70)))
    }

    let radius = Math.min(widthPx, heightPx) / 10

    if (this.signType === "ecoboard" || (this.signType === "color_badge" && this.state.material === "ecoboard")) {
      return Math.max(radius, Calc.mmToPx(4))
    }

    if (
      ["forex_sign", "solid_plastic_sign"].includes(this.signType) ||
      (this.signType === "double_sided_sign" && material === "plastic")
    ) {
      return Math.max(radius, Calc.mmToPx(5))
    }

    if (fixmethod === "military") {
      radius *= 0.8
    }

    if (this.signType === "stainless_steel_sign" || (this.signType === "label_plates" && material === "stainless")) {
      if (this.state.shape === "round") {
        return Calc.mmToPx(3.2)
      } else if (this.state.shape === "rect") {
        return Calc.mmToPx(2)
      }
    }

    if (this.signType === "thermal_transfer_label") {
      return Calc.mmToPx(1.65)
    }

    return radius
  }

  private flanged(width: number, height: number, offset: number, rounding?: number) {
    const flangedSmall = this.state.height < 300
    const signWidth = this.state.width
    rounding = rounding ? rounding : 0

    const offsetX = flangedSmall ? this.flangedHeight : offset + rounding
    const offsetY = flangedSmall ? rounding : offset + rounding

    const round = rounding > 0
    const x = 0
    const y = 0
    const commands = []
    // Make the cut angle 45 degree based on the sign width and whether it is flanged small and round
    const cutAngle = signWidth >= 600 && flangedSmall && round ? (signWidth >= 1000 ? 90 : 45) : 0

    commands.push("M" + (x - width / 2 + offsetX + cutAngle) + " " + (y - height / 2 - this.flangedHeight))
    commands.push("L" + (x + width / 2 - offsetX - cutAngle) + " " + (y - height / 2 - this.flangedHeight))

    if (round) {
      commands.push("L" + (x + width / 2 - rounding) + " " + (y - height / 2))
      commands.push("A" + rounding + " " + rounding + " 0 0 1 " + (x + width / 2) + " " + (y - height / 2 + rounding))
    }

    commands.push("L" + (x + width / 2 + (flangedSmall ? 0 : this.flangedHeight)) + " " + (y - height / 2 + offsetY))
    commands.push("L" + (x + width / 2 + (flangedSmall ? 0 : this.flangedHeight)) + " " + (y + height / 2 - offsetY))

    if (round) {
      commands.push("L" + (x + width / 2) + " " + (y + height / 2 - rounding))
      commands.push("A" + rounding + " " + rounding + " 0 0 1 " + (x + width / 2 - rounding) + " " + (y + height / 2))
    }

    commands.push("L" + (x + width / 2 - offsetX - cutAngle) + " " + (y + height / 2 + this.flangedHeight))
    commands.push("L" + (x - width / 2 + offsetX + cutAngle) + " " + (y + height / 2 + this.flangedHeight))

    if (round) {
      commands.push("L" + (x - width / 2 + rounding) + " " + (y + height / 2))
      commands.push("A" + rounding + " " + rounding + " 0 0 1 " + (x - width / 2) + " " + (y + height / 2 - rounding))
    }

    commands.push("L" + (x - width / 2 - (flangedSmall ? 0 : this.flangedHeight)) + " " + (y + height / 2 - offsetY))
    commands.push("L" + (x - width / 2 - (flangedSmall ? 0 : this.flangedHeight)) + " " + (y - height / 2 + offsetY))

    if (round) {
      commands.push("L" + (x - width / 2) + " " + (y - height / 2 + rounding))
      commands.push("A" + rounding + " " + rounding + " 0 0 1 " + (x - width / 2 + rounding) + " " + (y - height / 2))
    }

    commands.push("Z")

    return new PathObject(commands.join(" "), this.defaultParams)
  }
}
